import {
  ReleaseResourceAllocationType,
  ResourceAllocationStatus
} from '~/types';
import {
  invokeUpdateResourceAllocationAggregateCache,
  updateProjectTotalsCacheForNewAllocation
} from '~/modules/resourcing/enhancers/updateAllocationTotalsCache';
import { mapIsoStringtoUtcObject } from '~/modules/common/dates/convert';
import { getSessionStorage } from '~/modules/common/hooks';
import { getTotalHoursAndCostFromAllocations } from '~/modules/resourcing/util/getTotalHoursAndCostFromAllocations';
import { getUpdatedAllocatedUserListFromAllocations } from '~/modules/resourcing/common/util/allocationCacheUpdates';
import { getCostAndHoursDelta } from '~/modules/resourcing/util/getCostAndHoursDelta';
import {
  tryResourcePlanAllocatedUsersAndRolesCachedQuery,
  GET_RESOURCE_PLAN_ALLOCATED_USERS_AND_ROLES
} from '../../../hooks/useFetchAllocatedUsersAndRoles';
import { GET_RESOURCE_PLAN_ALLOCATED_USERS_AND_ROLES_VARIABLES } from '../../../hooks/useFetchResourcePlanUsersAndRoles';
import ALLOCATIONS_QUERY from './useProjectCommitedAllocationQuery';

export const tryLoadCachedQuery = ({ proxy, variables }) => {
  try {
    return proxy.readQuery({
      query: ALLOCATIONS_QUERY,
      variables
    });
  } catch {
    return null;
  }
};

export const writeAllocationsQuery = ({
  limit,
  projectUri,
  allocationStatusList,
  proxy,
  resourceAllocations,
  chartDateRange,
  showTimeOff,
  showHolidays,
  filter,
  sort
}) => {
  const variables = {
    limit,
    projectUri,
    allocationStatusList,
    showTimeOff,
    chartDateRange,
    showHolidays,
    filter,
    sort
  };

  const results = tryLoadCachedQuery({
    proxy,
    variables
  });

  if (!results) return;

  const {
    resourceAllocations: {
      resourceAllocations: cachedAllocations,
      nextPageCursor
    }
  } = results;

  if (!cachedAllocations) return;

  proxy.writeQuery({
    query: ALLOCATIONS_QUERY,
    variables,
    data: {
      resourceAllocations: {
        resourceAllocations: [
          ...cachedAllocations,
          ...resourceAllocations.map(r => ({
            ...r,
            scheduleDurationByDay: r.scheduleDurationByDay || []
          }))
        ],
        nextPageCursor,
        __typename: 'ResourceAllocationsResponse'
      }
    }
  });
};

export const useUpdateCompletedResourceAllocationProjectCacheOnAddWithoutResourceRequest = ({
  projectUri,
  limit = 200,
  allocationStatusList = [ResourceAllocationStatus.Committed],
  chartDateRange,
  showTimeOff,
  showHolidays,
  filter,
  sort,
  resourceCostMode
}) => (proxy, mutationResponse) => {
  const {
    data: {
      createFulfilledResourceAllocationWithoutResourceRequest: {
        resourceAllocation
      }
    }
  } = mutationResponse;

  writeAllocationsQuery({
    projectUri,
    limit,
    allocationStatusList,
    proxy,
    resourceAllocations: [resourceAllocation],
    chartDateRange,
    showTimeOff,
    showHolidays,
    filter,
    sort
  });
  updateProjectTotalsCacheForNewAllocation({
    proxy,
    newAllocation: resourceAllocation,
    resourceCostMode,
    filter
  });
};

export const useUpdateCompletedResourceAllocationProjectCacheOnAddWithProposedRequest = ({
  projectUri,
  limit = 200,
  allocationStatusList = [ResourceAllocationStatus.Committed],
  chartDateRange,
  showTimeOff,
  showHolidays,
  filter,
  sort
}) => () => (proxy, mutationResponse) => {
  const {
    data: {
      markResourceRequestAsComplete2: {
        resourceRequest: { resourceAllocations }
      }
    }
  } = mutationResponse;

  writeAllocationsQuery({
    projectUri,
    limit,
    allocationStatusList,
    proxy,
    resourceAllocations,
    chartDateRange,
    showTimeOff,
    showHolidays,
    filter,
    sort
  });
};

export const useUpdateCompletedResourceAllocationProjectCacheOnAddWithProposedRequest2 = ({
  allocationChartRef
}) => () => (proxy, mutationResponse) => {
  const {
    data: {
      markResourceRequestAsComplete2: {
        isOptimistic,
        resourceRequest: { resourceAllocations }
      }
    }
  } = mutationResponse;

  if (isOptimistic) return;

  const newUsers = resourceAllocations.map(allocation => allocation.user);

  const variables = getSessionStorage(
    GET_RESOURCE_PLAN_ALLOCATED_USERS_AND_ROLES_VARIABLES,
    null
  );

  const cachedResults = tryResourcePlanAllocatedUsersAndRolesCachedQuery({
    proxy,
    variables
  });

  const userList = cachedResults
    ? getUpdatedAllocatedUserListFromAllocations({
        newAllocations: resourceAllocations,
        cachedAllocatedUsers:
          cachedResults.project.resourcePlanAllocatedUsersAndRoles
      })
    : [];

  proxy.writeQuery({
    query: GET_RESOURCE_PLAN_ALLOCATED_USERS_AND_ROLES,
    variables,
    data: {
      project: {
        ...cachedResults?.project,
        resourcePlanAllocatedUsersAndRoles: userList
      }
    }
  });

  allocationChartRef?.current.handleResourceAllocationUserChange({
    newUsers: newUsers.map(user => user.user)
  });
  allocationChartRef?.current.handleLoadingStateOnResourceRequestAccept({
    newUsers
  });
};

export const getUpdateCompletedResourceAllocationProjectCacheOnEdit = ({
  projectUri,
  limit = 200,
  sort,
  allocationStatusList = [ResourceAllocationStatus.Committed],
  chartDateRange,
  showTimeOff,
  showHolidays,
  filter
}) => (proxy, mutationResponse) => {
  const {
    data: {
      updateResourceAllocation2: { resourceAllocation }
    }
  } = mutationResponse;

  const variables = {
    limit,
    projectUri,
    sort,
    allocationStatusList,
    chartDateRange,
    showTimeOff,
    showHolidays,
    filter
  };

  const results = tryLoadCachedQuery({
    proxy,
    variables
  });

  if (!results) return;

  const {
    resourceAllocations: {
      resourceAllocations: cachedAllocations,
      nextPageCursor
    }
  } = results;

  const allocationIndex = cachedAllocations.findIndex(
    alloc => alloc.id === resourceAllocation.id
  );

  const resourceAllocations = [...cachedAllocations];

  if (allocationIndex > -1) {
    resourceAllocations.splice(allocationIndex, 1, resourceAllocation);
  }
  proxy.writeQuery({
    query: ALLOCATIONS_QUERY,
    variables,
    data: {
      resourceAllocations: {
        resourceAllocations,
        nextPageCursor
      }
    }
  });
};

export const useUpdateCompletedResourceAllocationProjectCacheOnDelete = ({
  projectUri,
  limit = 200,
  allocationStatusList = [ResourceAllocationStatus.Committed],
  chartDateRange,
  showTimeOff,
  showHolidays,
  filter,
  sort,
  resourceCostMode
}) => ({ releaseType, releaseDate, initialTotalAllocationsCostAndHours }) => (
  proxy,
  mutationResponse
) => {
  const {
    data: {
      releaseResourceAllocation: { resourceAllocation }
    }
  } = mutationResponse;

  const variables = {
    limit,
    projectUri,
    allocationStatusList,
    chartDateRange,
    showTimeOff,
    showHolidays,
    filter,
    sort
  };

  if (initialTotalAllocationsCostAndHours) {
    const afterReleaseTotalAllocationsCostAndHours =
      releaseType === ReleaseResourceAllocationType.Entirely
        ? {
            totalHours: 0,
            totalCost: { currency: null, amount: 0 }
          }
        : getTotalHoursAndCostFromAllocations({
            allocations: [resourceAllocation],
            resourceCostMode
          });

    const costAndHoursDelta = getCostAndHoursDelta({
      newTotalCost: afterReleaseTotalAllocationsCostAndHours.totalCost,
      newHours: afterReleaseTotalAllocationsCostAndHours.totalHours,
      initialTotalCost: initialTotalAllocationsCostAndHours.totalCost,
      initialHours: initialTotalAllocationsCostAndHours.totalHours
    });

    invokeUpdateResourceAllocationAggregateCache({
      proxy,
      projectId: projectUri,
      costAndHoursDelta,
      resourceCostMode,
      filter: filter || {}
    });
  }

  const results = tryLoadCachedQuery({
    proxy,
    variables
  });

  if (!results) return;

  const {
    resourceAllocations: {
      resourceAllocations: cachedAllocations,
      nextPageCursor
    }
  } = results;

  const startDateInFuture =
    mapIsoStringtoUtcObject(resourceAllocation.startDate) >
    mapIsoStringtoUtcObject(releaseDate);

  proxy.writeQuery({
    query: ALLOCATIONS_QUERY,
    variables,
    data: {
      resourceAllocations: {
        resourceAllocations: cachedAllocations.filter(
          alloc =>
            alloc.id !== resourceAllocation.id ||
            (alloc.id === resourceAllocation.id &&
              releaseType === ReleaseResourceAllocationType.Asof &&
              !startDateInFuture)
        ),
        nextPageCursor,
        __typename: 'ResourceAllocationsResponse'
      }
    }
  });
};
